import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {DocumentSnapshot} from 'firebase/firestore';
import {Company, User} from 'hubbl-shared';
import {
  addHRIStoCompany,
  addCompanyDepartment,
  fetchCompany,
  fetchCompanyEmployees,
  fetchAllCompanyEmployees,
} from './CompanyThunks';

interface CompanyState {
  employeeOverview: {
    employees: User[];
    lastVisible: DocumentSnapshot<User> | null;
    loading: boolean;
    error: string | null;
    hasMore: boolean;
  };
  companyDetails: Company | null;
  apideckSession: {
    session_token: string | null;
    session_uri: string | null;
  };
}

const initialState: CompanyState = {
  employeeOverview: {
    employees: [],
    lastVisible: null,
    loading: false,
    error: null,
    hasMore: true,
  },
  companyDetails: null,
  apideckSession: {
    session_token: null,
    session_uri: null,
  },
};

export const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    // setUserDetails: (state, action: PayloadAction<User>) => {
    //   state.userDetails = action.payload;
    // },
    resetCompanyDetails: state => {
      state.employeeOverview = {
        employees: [],
        lastVisible: null,
        loading: false,
        error: null,
        hasMore: true,
      };
      state.companyDetails = null;
    },
    setApideckSession: (
      state,
      action: PayloadAction<{session_token: string; session_uri: string}>,
    ) => {
      state.apideckSession = action.payload;
    },
    removeEmployee: (state, action: PayloadAction<string>) => {
      state.employeeOverview.employees =
        state.employeeOverview.employees.filter(
          employee => employee.id !== action.payload,
        );
    },
    addEmployee: (state, action: PayloadAction<User>) => {
      // Only add if not already present
      const exists = state.employeeOverview.employees.some(
        emp => emp.id === action.payload.id,
      );
      if (!exists) {
        state.employeeOverview.employees.push(action.payload);
      }
    },
    updateEmployee: (state, action: PayloadAction<User>) => {
      const index = state.employeeOverview.employees.findIndex(
        emp => emp.id === action.payload.id,
      );
      if (index !== -1) {
        state.employeeOverview.employees[index] = action.payload;
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchCompany.fulfilled, (state, action) => {
      state.companyDetails = action.payload;
    });
    builder
      .addCase(addHRIStoCompany.fulfilled, (state, action) => {
        if (state.companyDetails) {
          state.companyDetails.settings.connectedHRIS = action.payload;
        }
      })
      .addCase(addCompanyDepartment.fulfilled, (state, action) => {
        if (state.companyDetails) {
          // Add the new department to the company's departments array
          const existingDepartments = state.companyDetails.departments || [];
          // Check if department already exists to avoid duplicates
          const departmentExists = existingDepartments.some(
            dept => dept.id === action.payload.id,
          );
          if (!departmentExists) {
            state.companyDetails.departments = [
              ...existingDepartments,
              action.payload,
            ];
          }
        }
      })
      .addCase(fetchCompanyEmployees.pending, state => {
        state.employeeOverview.loading = true;
        state.employeeOverview.error = null;
      })
      .addCase(fetchCompanyEmployees.fulfilled, (state, action) => {
        state.employeeOverview.loading = false;
        state.employeeOverview.hasMore = action.payload.lastVisible !== null;

        // Check if it's the next page or first page
        if (action.payload.isNextPage) {
          state.employeeOverview.employees = [
            ...state.employeeOverview.employees,
            ...action.payload.employees,
          ]; // Append new events for pagination
        } else {
          state.employeeOverview.employees = action.payload.employees; // Overwrite events on the first page load
        }

        state.employeeOverview.lastVisible = action.payload.lastVisible; // Update the lastVisible for pagination
      })
      .addCase(fetchCompanyEmployees.rejected, (state, action) => {
        state.employeeOverview.loading = false;
        state.employeeOverview.hasMore = false;
        state.employeeOverview.error =
          action.error.message || 'Error fetching employees';
      })
      .addCase(fetchAllCompanyEmployees.pending, state => {
        state.employeeOverview.loading = true;
        state.employeeOverview.error = null;
      })
      .addCase(fetchAllCompanyEmployees.fulfilled, (state, action) => {
        state.employeeOverview.loading = false;
        state.employeeOverview.employees = action.payload.employees;
        state.employeeOverview.hasMore = false; // No more pages when all employees are loaded
        state.employeeOverview.lastVisible = null;
      })
      .addCase(fetchAllCompanyEmployees.rejected, (state, action) => {
        state.employeeOverview.loading = false;
        state.employeeOverview.error =
          action.error.message || 'Error fetching all employees';
      });
  },
});

export const {
  resetCompanyDetails,
  setApideckSession,
  removeEmployee,
  addEmployee,
  updateEmployee,
} = companySlice.actions;

export const CompanyReducer = companySlice.reducer;
